import React, { useRef, useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Download, Eye, Printer, FileText, CheckCircle, Clock, Shield, Loader2 } from 'lucide-react';
import QRCode from 'react-qr-code';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { getToken } from '../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Composant pour afficher la convention en format imprimable/PDF
export const ConventionDocument = React.forwardRef(({ convention, showSignatures = true }, ref) => {
  const getVerificationUrl = () => {
    return `${window.location.origin}/verify/${convention?.id || 'unknown'}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div ref={ref} className="bg-white p-8 max-w-4xl mx-auto" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* En-tête du document */}
      <div className="border-b-2 border-blue-800 pb-4 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-blue-900">COPPAM</h1>
            <p className="text-sm text-slate-600">Garanties Solidaires</p>
            <p className="text-xs text-slate-500">Économie Sociale et Solidaire</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold">Référence: {convention?.numero_contrat}</p>
            <p className="text-xs text-slate-600">Émis le: {formatDate(convention?.created_at)}</p>
            {convention?.date_signature && (
              <Badge className="mt-1 bg-green-600 text-white text-xs font-semibold">Signé</Badge>
            )}
          </div>
        </div>
      </div>

      {/* Titre de la convention */}
      <div className="text-center mb-8">
        <h2 className="text-xl font-bold text-slate-800 uppercase">
          {convention?.titre || 'Convention de Partenariat'}
        </h2>
        <p className="text-sm text-slate-600 mt-2">
          Type: {convention?.type_convention === 'cooper' ? "COOPER'S" : 
                 convention?.type_convention === 'expert' ? "EXPERT" : "Partenaire"}
          {' • '}Durée: {convention?.duree_mois || 12} mois
        </p>
      </div>

      {/* Parties */}
      <div className="mb-6 p-4 bg-slate-50 rounded-lg">
        <h3 className="font-bold text-slate-800 mb-2">ENTRE LES SOUSSIGNÉS :</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="font-semibold">COPPAM - Garanties Solidaires</p>
            <p className="text-slate-600">Représentée par F. PIPEROL, Présidente</p>
            <p className="text-slate-500 text-xs">Ci-après dénommé "l'Émetteur"</p>
          </div>
          <div>
            <p className="font-semibold">
              {convention?.destinataire_nom || 
               (convention?.destinataire_autre ? 
                `${convention.destinataire_autre.prenom} ${convention.destinataire_autre.nom}` : 
                'Le Destinataire')}
            </p>
            {convention?.destinataire_autre?.entreprise && (
              <p className="text-slate-600">{convention.destinataire_autre.entreprise}</p>
            )}
            <p className="text-slate-500 text-xs">Ci-après dénommé "le Partenaire"</p>
          </div>
        </div>
      </div>

      {/* Contenu de la convention */}
      <div className="mb-8 prose prose-sm max-w-none">
        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(convention?.contenu_html || '<p>Contenu non disponible</p>') }} />
      </div>

      {/* Conditions spécifiques */}
      {convention?.conditions_specifiques && (
        <div className="mb-8 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
          <h3 className="font-bold text-slate-800 mb-2">CONDITIONS SPÉCIFIQUES</h3>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{convention.conditions_specifiques}</p>
        </div>
      )}

      {/* Bloc signatures */}
      {showSignatures && (
        <div className="mt-10 pt-6 border-t-2 border-dashed border-slate-300">
          <h3 className="text-center font-bold text-slate-800 mb-6">SIGNATURES</h3>
          
          <div className="grid grid-cols-2 gap-4 md:p-8">
            {/* Signature COPPAM */}
            <div className="text-center p-4 border rounded-lg bg-blue-50">
              <p className="font-semibold text-blue-900 mb-4">COPPAM - Garanties Solidaires</p>
              
              {/* Tampon SVG simplifié pour PDF */}
              <div className="flex justify-center mb-3">
                <svg width="100" height="100" viewBox="0 0 250 250">
                  <circle cx="125" cy="125" r="118" fill="none" stroke="#1e40af" strokeWidth="3" />
                  <circle cx="125" cy="125" r="105" fill="none" stroke="#1e40af" strokeWidth="1.5" />
                  <circle cx="125" cy="125" r="55" fill="#1e40af" opacity="0.1" />
                  <text x="125" y="115" textAnchor="middle" fill="#1e40af" fontSize="20" fontWeight="bold">COPPAM</text>
                  <text x="125" y="138" textAnchor="middle" fill="#1e40af" fontSize="9">Garanties Solidaires</text>
                  <text x="125" y="155" textAnchor="middle" fill="#1e40af" fontSize="8">ESS - DOM-TOM</text>
                </svg>
              </div>

              {/* Signature */}
              <div className="mb-2">
                <svg width="150" height="50" viewBox="0 0 200 60" className="mx-auto">
                  <path
                    d="M 10,40 Q 20,20 35,25 Q 50,30 45,45 M 35,25 L 55,25 M 65,50 Q 60,30 75,25 Q 90,20 95,35 Q 100,45 115,40 M 105,25 L 105,50 M 125,35 Q 130,25 145,30 Q 150,35 145,45 Q 140,50 125,45 M 160,50 Q 155,30 170,25 Q 185,20 190,35 Q 195,45 205,40"
                    fill="none" stroke="#1e3a5f" strokeWidth="2" strokeLinecap="round"
                  />
                </svg>
                <p className="text-sm font-semibold text-blue-900">F. PIPEROL</p>
                <p className="text-xs text-slate-600">Présidente</p>
              </div>

              {convention?.date_envoi && (
                <div className="text-xs text-slate-500 mt-2">
                  <p>Signé le {formatDate(convention.date_envoi)}</p>
                </div>
              )}

              {/* QR Code */}
              {convention?.date_envoi && (
                <div className="mt-3 flex justify-center">
                  <div className="p-2 bg-white rounded border">
                    <QRCode value={getVerificationUrl()} size={50} level="M" />
                  </div>
                </div>
              )}
            </div>

            {/* Signature Destinataire */}
            <div className="text-center p-4 border rounded-lg bg-green-50">
              <p className="font-semibold text-green-900 mb-4">
                {convention?.type_convention === 'cooper' ? "COOPER'S" : 
                 convention?.type_convention === 'expert' ? "EXPERT" : "Partenaire"}
              </p>
              
              {convention?.date_signature ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="h-10 w-10 text-green-600" />
                  </div>
                  
                  <p className="text-sm font-semibold text-green-900">
                    {convention?.signature_electronique || convention?.destinataire_nom || 'Signataire'}
                  </p>
                  <p className="text-xs text-slate-600 mt-1">Signature électronique</p>
                  
                  <div className="text-xs text-slate-500 mt-2">
                    <p>Signé le {formatDate(convention.date_signature)}</p>
                  </div>

                  {/* QR Code */}
                  <div className="mt-3 flex justify-center">
                    <div className="p-2 bg-white rounded border">
                      <QRCode value={`${getVerificationUrl()}?sig=dest`} size={50} level="M" />
                    </div>
                  </div>
                </>
              ) : (
                <div className="py-4 md:py-8">
                  <Clock className="h-10 w-10 mx-auto text-orange-500 mb-2" />
                  <p className="text-sm text-orange-700">En attente de signature</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pied de page */}
      <div className="mt-10 pt-4 border-t text-center text-xs text-slate-500">
        <p>Document généré électroniquement par COPPAM - Garanties Solidaires</p>
        <p>Référence: {convention?.numero_contrat} • ID: {convention?.id}</p>
        <p className="mt-1">
          Ce document a valeur légale conformément au règlement eIDAS sur les signatures électroniques.
        </p>
      </div>
    </div>
  );
});

ConventionDocument.displayName = 'ConventionDocument';

// Composant pour les actions de téléchargement/consultation
export default function ConventionDownload({ convention, variant = 'button' }) {
  const [showPreview, setShowPreview] = useState(false);
  const [generating, setGenerating] = useState(false);
  const _documentRef = useRef(null);

  // V36.70 — PDF généré côté backend (WeasyPrint), remplace html2canvas+jsPDF
  // qui échouait en PROD (polices, SVG, CSP). On télécharge le PDF via fetch
  // avec auth Bearer + on déclenche un download via blob URL.
  const handleDownloadPDF = async () => {
    if (!convention?.id) {
      alert('Convention sans identifiant : impossible de générer le PDF.');
      return;
    }
    setGenerating(true);
    try {
      const token = getToken();
      const url = `${API}/conventions/${encodeURIComponent(convention.id)}/pdf?download=1`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        let detail = '';
        try { detail = (await res.json())?.detail || ''; } catch { /* noop */ }
        throw new Error(`HTTP ${res.status}${detail ? ' — ' + detail : ''}`);
      }
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `Convention_${convention.numero_contrat || convention.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Libérer le blob après un court délai (Safari)
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[ConventionDownload] PDF generation failed:', error);
      const msg = (error && (error.message || error.toString())) || 'erreur inconnue';
      alert(`Erreur lors de la génération du PDF :\n${msg}`);
    } finally {
      setGenerating(false);
    }
  };

  // V36.70 — Imprimer = ouvrir le PDF backend dans un nouvel onglet, l'utilisateur
  // utilise le bouton « Imprimer » natif du visualiseur PDF (et peut aussi enregistrer).
  const handlePrint = async () => {
    if (!convention?.id) {
      // Fallback : impression du dialog si l'ID manque
      setShowPreview(true);
      setTimeout(() => { try { window.print(); } catch { /* noop */ } }, 500);
      return;
    }
    try {
      const token = getToken();
      const url = `${API}/conventions/${encodeURIComponent(convention.id)}/pdf`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
      if (!win) {
        // Popup bloquée : fallback download
        const a = document.createElement('a');
        a.href = blobUrl;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[ConventionDownload] Print failed:', error);
      // Fallback : impression du dialog
      setShowPreview(true);
      setTimeout(() => { try { window.print(); } catch { /* noop */ } }, 500);
    }
  };

  if (variant === 'compact') {
    return (
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowPreview(true)}
          className="text-blue-600 border-blue-600 hover:bg-blue-50"
        >
          <Eye className="h-4 w-4 mr-1" />
          Consulter
        </Button>
        <Button
          size="sm"
          onClick={handleDownloadPDF}
          disabled={generating}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          {generating ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-1" />
          )}
          PDF
        </Button>

        {/* Modal de prévisualisation */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Convention - {convention?.numero_contrat}
              </DialogTitle>
            </DialogHeader>
            
            <div className="flex gap-2 mb-4">
              <Button onClick={handleDownloadPDF} disabled={generating} className="bg-green-600 hover:bg-green-700">
                {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                Télécharger PDF
              </Button>
              <Button variant="outline" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-2" />
                Imprimer
              </Button>
            </div>

            <div className="border rounded-lg shadow-inner bg-gray-100 p-4">
              <ConventionDocument convention={convention} showSignatures={true} />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Boutons d'action */}
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={() => setShowPreview(true)}
          variant="outline"
          className="border-blue-600 text-blue-600 hover:bg-blue-50"
        >
          <Eye className="h-4 w-4 mr-2" />
          Consulter la convention
        </Button>
        
        <Button
          onClick={handleDownloadPDF}
          disabled={generating}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          {generating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Génération...
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Télécharger PDF
            </>
          )}
        </Button>

        <Button
          onClick={handlePrint}
          variant="outline"
        >
          <Printer className="h-4 w-4 mr-2" />
          Imprimer
        </Button>
      </div>

      {/* Informations sur le document */}
      <div className="flex items-center gap-4 text-sm text-slate-600">
        <div className="flex items-center gap-1">
          <Shield className="h-4 w-4 text-green-600" />
          <span>Document signé électroniquement</span>
        </div>
        <div className="flex items-center gap-1">
          <FileText className="h-4 w-4" />
          <span>Réf: {convention?.numero_contrat}</span>
        </div>
      </div>

      {/* Modal de prévisualisation */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto print:max-w-none print:max-h-none">
          <DialogHeader className="print:hidden">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Convention signée - {convention?.numero_contrat}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex gap-2 mb-4 print:hidden">
            <Button onClick={handleDownloadPDF} disabled={generating} className="bg-green-600 hover:bg-green-700">
              {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Télécharger PDF
            </Button>
            <Button variant="outline" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />
              Imprimer
            </Button>
          </div>

          <div className="border rounded-lg shadow-inner bg-gray-100 p-4 print:border-none print:shadow-none print:p-0 print:bg-white">
            <ConventionDocument convention={convention} showSignatures={true} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
